import React, { useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { AssessorAuthContext } from '../context/AssessorAuthContext';
import { ThemeContext } from '../context/ThemeContext';
import './Login.css';

// Address of the separate Multi-Centre TVET Platform. Set REACT_APP_NEW_PLATFORM_URL when building for
// production; in development it defaults to the local platform server. When empty, the button is hidden.
const RAW_PLATFORM_URL =
  process.env.REACT_APP_NEW_PLATFORM_URL ||
  (process.env.NODE_ENV !== 'production' ? 'http://localhost:5100' : '');
// a bare domain such as tvethub.up.railway.app would otherwise be treated as a relative link
const NEW_PLATFORM_URL = RAW_PLATFORM_URL && !/^https?:\/\//i.test(RAW_PLATFORM_URL) ? `https://${RAW_PLATFORM_URL}` : RAW_PLATFORM_URL;

// Password box with an eye button that shows or hides what was typed.
const PasswordInput = (props) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="password-wrap">
      <input {...props} type={shown ? 'text' : 'password'} />
      <button type="button" className="eye" aria-label={shown ? 'Hide password' : 'Show password'} aria-pressed={shown} onClick={() => setShown(!shown)}>
        {shown ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
        )}
      </button>
    </div>
  );
};

const Login = () => {
  const { login, user } = useContext(AuthContext);
  const { assessor, login: assessorLogin } = useContext(AssessorAuthContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [portal, setPortal] = useState(searchParams.get('portal') === 'assessor' ? 'assessor' : 'admin');
  const [logoError, setLogoError] = useState(false);

  // --- Admin sign-in state ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login');
  const [code, setCode] = useState('');
  const [verifiedCode, setVerifiedCode] = useState('');
  const [codeMessage, setCodeMessage] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [adminRecoveryEmail, setAdminRecoveryEmail] = useState('');

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    // Reflects whatever ADMIN_EMAIL is currently set in Railway — no rebuild
    // needed when it changes, since this is fetched at runtime, not baked in.
    axios.get('/api/auth/forgot-password/admin-email')
      .then((res) => setAdminRecoveryEmail(res.data.email || ''))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email.trim(), password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleSendCode = async () => {
    setCodeMessage('');
    setSendingCode(true);
    try {
      await axios.post('/api/auth/forgot-password/send-code');
      setCodeMessage('Verification code sent to your admin email. Please check your inbox.');
    } catch (err) {
      setCodeMessage(err.response?.data?.error || 'Unable to send verification code.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setCodeMessage('Enter the verification code you received.');
      return;
    }
    setVerifyingCode(true);
    try {
      const trimmedCode = code.trim();
      await axios.post('/api/auth/forgot-password/verify-code', { code: trimmedCode });
      setCodeMessage('Verification successful. Please create a new password.');
      setVerifiedCode(trimmedCode);
      setCode('');
      setView('reset');
    } catch (err) {
      setCodeMessage(err.response?.data?.error || 'Invalid verification code.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResetPassword = async () => {
    if (!verifiedCode) {
      setCodeMessage('Please verify your code first.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setCodeMessage('Enter and confirm your new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setCodeMessage('Passwords do not match.');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post('/api/auth/forgot-password/reset', {
        code: verifiedCode,
        newPassword,
        confirmPassword
      });
      setCodeMessage('Password updated successfully. You can now log in.');
      setTimeout(() => {
        resetForgotState();
      }, 2000);
    } catch (err) {
      setCodeMessage(err.response?.data?.error || 'Unable to reset password.');
    } finally {
      setResettingPassword(false);
    }
  };

  const resetForgotState = () => {
    setView('login');
    setCode('');
    setVerifiedCode('');
    setCodeMessage('');
    setSendingCode(false);
    setVerifyingCode(false);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  // --- Assessor sign-in state ---
  const [assessorRosterStatus, setAssessorRosterStatus] = useState('loading'); // loading | ready | error
  const [assessorRosterError, setAssessorRosterError] = useState('');
  const [assessorList, setAssessorList] = useState([]);
  const [assessorId, setAssessorId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [assessorError, setAssessorError] = useState('');
  const [assessorSubmitting, setAssessorSubmitting] = useState(false);

  useEffect(() => {
    if (assessor) navigate('/assessor/dashboard');
  }, [assessor, navigate]);

  const loadRoster = useCallback(() => {
    setAssessorRosterStatus('loading');
    axios
      .get('/api/assessor-auth/roster')
      .then((res) => {
        setAssessorList(res.data.assessors || []);
        setAssessorRosterStatus('ready');
      })
      .catch((err) => {
        setAssessorRosterStatus('error');
        setAssessorRosterError(err.response?.data?.error || 'Unable to load assessors right now.');
      });
  }, []);

  useEffect(() => {
    if (portal === 'assessor') loadRoster();
  }, [portal, loadRoster]);

  const handleAssessorSubmit = async (e) => {
    e.preventDefault();
    if (!assessorId || !accessCode.trim()) {
      setAssessorError('Select your name and enter your access code');
      return;
    }
    setAssessorSubmitting(true);
    setAssessorError('');
    try {
      const res = await axios.post('/api/assessor-auth/login', {
        assessor_id: assessorId,
        access_code: accessCode.trim(),
      });
      assessorLogin(res.data.token, res.data.assessor);
      navigate('/assessor/dashboard', { replace: true });
    } catch (err) {
      setAssessorError(err.response?.data?.error || 'Invalid access code');
    } finally {
      setAssessorSubmitting(false);
    }
  };

  const switchPortal = (next) => {
    setPortal(next);
    setError('');
    setAssessorError('');
  };

  return (
    <div className="auth-page" data-theme={theme}>
      <div className="auth-card">
        <div className="auth-visual-panel">
          <div className="auth-visual-content">
            <div className="login-logo-container">
              {!logoError ? (
                <img
                  src="/logo/css-farms-tvet-logo.png"
                  alt="CSS FARMS IDEAS-TVET Logo"
                  className="login-logo"
                  onError={() => setLogoError(true)}
                  onLoad={() => setLogoError(false)}
                />
              ) : (
                <div className="login-logo-placeholder">
                  <span>IDEAS</span>
                </div>
              )}
            </div>

            {portal === 'admin' ? (
              <>
                <h1 className="auth-title">Welcome back</h1>
                <p className="auth-subtitle">CSS FARMS IDEAS-TVET Trainee Management System</p>
              </>
            ) : (
              <>
                <h1 className="auth-title">Assessor Sign In</h1>
                <p className="auth-subtitle">CSS Evidence Book Portal</p>
              </>
            )}

            <span className="auth-visual-divider" />

            <p className="auth-visual-tagline">
              {portal === 'admin'
                ? 'Manage trainees, attendance, and outcomes — all in one place.'
                : 'Record workbook, personal statement, and oral Q&A evidence, unit by unit.'}
            </p>
          </div>

          <div className="login-footer">
            <p>Powered by <strong>BURST</strong> - <strong>BRAIN</strong> <strong>CONCEPT 2026</strong></p>
          </div>
        </div>

        <div className="auth-form-panel">
          <div className="auth-form-inner">
            <div className="portal-toggle" role="tablist" aria-label="Choose sign-in portal">
              <div className={`portal-toggle-highlight ${portal === 'assessor' ? 'right' : ''}`} />
              <button
                type="button"
                role="tab"
                aria-selected={portal === 'admin'}
                className={`portal-toggle-btn ${portal === 'admin' ? 'active' : ''}`}
                onClick={() => switchPortal('admin')}
              >
                Admin
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={portal === 'assessor'}
                className={`portal-toggle-btn ${portal === 'assessor' ? 'active' : ''}`}
                onClick={() => switchPortal('assessor')}
              >
                Assessor
              </button>
            </div>

            {portal === 'admin' ? (
              <>
                {view === 'login' ? (
                  <form onSubmit={handleSubmit}>
                    {error && <div className="error-message">{error}</div>}
                    <div className="form-group">
                      <label>Admin</label>
                      <input
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter username or email"
                      />
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                      <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="Enter your password"
                      />
                    </div>
                    <div className="login-actions">
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => {
                          setView('forgot');
                          setEmail(adminRecoveryEmail);
                          setCode('');
                          setCodeMessage('');
                          setError('');
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Logging in...' : 'Login'}
                    </button>
                  </form>
                ) : view === 'forgot' ? (
                  <div className="forgot-panel">
                    <p>We'll send a verification code to the registered admin email.</p>
                    <div className="form-group">
                      <label>Admin Email (Fixed)</label>
                      <input
                        type="text"
                        value={adminRecoveryEmail}
                        readOnly
                        disabled
                        className="input-disabled"
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', opacity: 0.8 }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleSendCode}
                      disabled={sendingCode}
                    >
                      {sendingCode ? 'Sending code...' : 'Send Verification Code'}
                    </button>
                    <div className="form-group" style={{ marginTop: '15px' }}>
                      <label>Verification Code</label>
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Enter the 6-digit code"
                      />
                    </div>
                    <div className="forgot-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleVerifyCode}
                        disabled={verifyingCode || !code.trim()}
                      >
                        {verifyingCode ? 'Verifying...' : 'Verify Code'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleSendCode}
                        disabled={sendingCode}
                      >
                        Resend Code
                      </button>
                    </div>
                    {codeMessage && <div className="info-message">{codeMessage}</div>}
                    <button type="button" className="link-btn" onClick={() => {
                      resetForgotState();
                      setEmail('');
                    }}>
                      Back to login
                    </button>
                  </div>
                ) : view === 'reset' ? (
                  <div className="forgot-panel">
                    <p>Enter your new password below.</p>
                    <div className="form-group">
                      <label>New Password</label>
                      <PasswordInput
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter a new password"
                      />
                    </div>
                    <div className="form-group">
                      <label>Confirm Password</label>
                      <PasswordInput
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter the new password"
                      />
                    </div>
                    <div className="forgot-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleResetPassword}
                        disabled={resettingPassword}
                      >
                        {resettingPassword ? 'Saving...' : 'Save New Password'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setView('forgot')}
                        disabled={resettingPassword}
                      >
                        Back
                      </button>
                    </div>
                    {codeMessage && <div className="info-message">{codeMessage}</div>}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                {assessorRosterStatus === 'loading' ? (
                  <p className="auth-loading-text">Loading...</p>
                ) : assessorRosterStatus === 'error' ? (
                  <div className="error-message">{assessorRosterError}</div>
                ) : (
                  <form onSubmit={handleAssessorSubmit}>
                    {assessorError && <div className="error-message">{assessorError}</div>}
                    <div className="form-group">
                      <label>Your Name</label>
                      <select
                        value={assessorId}
                        onChange={(e) => setAssessorId(e.target.value)}
                        required
                        disabled={assessorSubmitting}
                      >
                        <option value="">-- Select your name --</option>
                        {assessorList.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Access Code</label>
                      <input
                        type="text"
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value)}
                        placeholder="Enter the code sent to your email/phone"
                        required
                        disabled={assessorSubmitting}
                        autoComplete="off"
                      />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={assessorSubmitting}>
                      {assessorSubmitting ? 'Signing in...' : 'Sign In'}
                    </button>
                  </form>
                )}
              </>
            )}

            {NEW_PLATFORM_URL && (
              <div className="new-platform-link">
                <p>Registering a training centre, or managing centre approvals and onboarding links?</p>
                <a className="new-platform-btn" href={NEW_PLATFORM_URL}>
                  Open the Multi-Centre TVET Platform
                  <span aria-hidden="true"> &rarr;</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
